export type CourseCertificateStatus =
  | "PENDING"
  | "GENERATING"
  | "ACTIVE"
  | "FAILED"
  | "REVOKED"
  | "EXPIRED";

export type CourseCertificate = {
  uuid: string;
  levelName: string;
  url: string | null;
  status?: CourseCertificateStatus;
};

export function isCertificatePreparing(
  cert: Pick<CourseCertificate, "url" | "status"> | null | undefined,
): boolean {
  if (!cert || cert.url) return false;
  return cert.status === "PENDING" || cert.status === "GENERATING";
}

export function resolveCertificateUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  const rawApiBaseUrl = (import.meta as any).env?.VITE_API_BASE_URL || (import.meta as any).env?.VITE_API_URL || "";
  const cleanedBaseUrl = rawApiBaseUrl.replace(/\/api\/v1\/?$/, "");
  return `${cleanedBaseUrl}${url.startsWith("/") ? "" : "/"}${url}`;
}

export function courseCertificatesNeedPolling(
  certificates: CourseCertificate[] | null | undefined,
): boolean {
  if (!certificates?.length) return false;
  return certificates.some((c) => isCertificatePreparing(c));
}

async function fetchCertificateBlob(uuid: string): Promise<Blob> {
  const { getCertificatePdfEndpointApiV1CertificatesCertificateUuidPdfGet } = await import(
    "@/src/api/sdk.gen"
  );
  const { data, error } = await getCertificatePdfEndpointApiV1CertificatesCertificateUuidPdfGet({
    path: { certificate_uuid: uuid },
  });
  if (error || !data) throw new Error("Failed to load certificate");
  return data as unknown as Blob;
}

/**
 * Downloads a certificate PDF via the authenticated SDK client (so the
 * Clerk bearer token is attached) and saves it as a file.
 *
 * A plain `<a href={certificate_url}>` doesn't carry auth headers — the
 * `/certificates/{uuid}/pdf` endpoint requires an authenticated owner, so
 * downloads must go through this instead.
 */
export async function downloadCertificatePdf(uuid: string, levelName?: string | null): Promise<void> {
  const blob = await fetchCertificateBlob(uuid);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `Certificate_${(levelName || "certificate").replace(/\s+/g, "_")}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/**
 * Opens a certificate PDF in a new browser tab (viewer's native PDF viewer,
 * which offers its own download/print controls) rather than force-downloading
 * it. Same auth constraint as `downloadCertificatePdf` — the PDF endpoint
 * requires a bearer token, so a plain `<a target="_blank" href=...>` can't be
 * used; the blob has to be fetched via the SDK first.
 *
 * The tab is opened synchronously (before the `await`) so browsers don't
 * treat it as an unsolicited popup and block it; its location is then
 * pointed at the blob URL once the fetch resolves.
 */
export async function viewCertificatePdf(uuid: string): Promise<void> {
  const newTab = window.open("", "_blank");
  try {
    const blob = await fetchCertificateBlob(uuid);
    const url = URL.createObjectURL(blob);
    if (newTab) {
      newTab.location.href = url;
    } else {
      // Popup blocked despite the synchronous open() attempt — fall back to
      // a direct open of the blob URL.
      window.open(url, "_blank");
    }
    // Revoke well after the tab has had time to load the PDF rather than
    // immediately, since the tab keeps referencing this blob: URL.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (err) {
    newTab?.close();
    throw err;
  }
}


